import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Avatar,
  alpha,
  useMediaQuery,
  useTheme,
  Stack,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Tabs,
  Tab,
  Badge,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Rating,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  AttachMoney as MoneyIcon,
  Comment as CommentIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  LocalShipping as LocalShippingIcon,
  Print as PrintIcon,
  Receipt as ReceiptIcon,
  Inventory as InventoryIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Approval as ApprovalIcon,
  Verified as VerifiedIcon,
  Work as WorkIcon,
  Store as StoreIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  History as HistoryIcon,
  VerifiedUser as VerifiedUserIcon,
} from '@mui/icons-material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineDot,
  TimelineConnector,
  TimelineContent,
  TimelineOppositeContent,
} from '@mui/lab';

import Sidebar from '../../../../components/sidebars/Sidebar_hotelAccount_cashier';
import TopNav from '../../../../components/TopNav_hotelStaff';

// ==================== CONSTANTS ====================
const REQUEST_STATUSES = {
  pending: { label: 'Pending', color: '#ff9800', icon: PendingIcon, action: 'Awaiting Account Approval' },
  account_approved: { label: 'Account Approved', color: '#2196f3', icon: CheckCircleIcon, action: 'Awaiting GM Approval' },
  gm_approved: { label: 'GM Approved', color: '#4caf50', icon: VerifiedIcon, action: 'Ready for Store Receipt' },
  partially_received: { label: 'Partially Received', color: '#ff9800', icon: LocalShippingIcon, action: 'Partial Delivery Received' },
  completed: { label: 'Completed', color: '#4caf50', icon: CheckCircleIcon, action: 'Fully Received' },
  rejected: { label: 'Rejected', color: '#f44336', icon: CancelIcon, action: 'Rejected by Approver' },
};

// Department is hardcoded for Store Keeping
const HARDCODED_DEPARTMENT = 'Store Keeping';

// ==================== HELPER FUNCTIONS ====================

const sanitizeString = (str) => {
  if (!str || typeof str !== 'string') return '';
  return str.trim()
    .replace(/[<>]/g, '')
    .replace(/&[a-z]+;/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/on\w+=/gi, '')
    .replace(/[\uD800-\uDFFF]/g, '');
};

const sanitizeNumber = (num) => {
  if (num === null || num === undefined || num === '') return 0;
  const parsed = parseFloat(num);
  return isNaN(parsed) ? 0 : parsed;
};

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '₦0.00';
  const num = parseFloat(amount);
  if (isNaN(num)) return '₦0.00';
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return 'N/A';
  }
};

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return 'N/A';
  }
};

const logSecurityEvent = (eventType, details) => {
  console.debug(`[SECURITY] ${eventType}:`, {
    timestamp: new Date().toISOString(),
    eventType,
    details,
    userAgent: navigator.userAgent,
  });
};

// Replace the existing validateAccountUser function with:

const validateAccountUser = (userType) => {
  // Convert to string for safety
  const userTypeStr = String(userType || '');
  
  // Define allowed account/finance roles (matching your login system)
  const validAccountTypes = [
    'Account',     
    
  ];
  
  // Check if user type matches any valid role (case-insensitive for flexibility)
  const isValid = validAccountTypes.some(type => 
    userTypeStr.toLowerCase() === type.toLowerCase()
  );
  
  console.debug('[AccountApproval] Role validation:', {
    originalUserType: userType,
    normalized: userTypeStr,
    isValid: isValid
  });
  
  return isValid;
};

// ==================== MAIN COMPONENT ====================
const PurshaseOrdeAccountApproval = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState(0);

  // Data states
  const [pendingRequests, setPendingRequests] = useState([]);
  const [approvedRequests, setApprovedRequests] = useState([]);
  const [rejectedRequests, setRejectedRequests] = useState([]);
  const [allRequests, setAllRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  // Dialog states
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [remarkHistoryDialogOpen, setRemarkHistoryDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Form states
  const [approvalRemark, setApprovalRemark] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  // User info
  const [userProfile, setUserProfile] = useState({
    phone: '',
    name: '',
    department: HARDCODED_DEPARTMENT,
    user_type: '',
  });

  // Chart/Summary states
  const [summaryData, setSummaryData] = useState({
    totalValue: 0,
    averageValue: 0,
    approvalRate: 0,
    totalRequests: 0,
  });

  const toggleSidebar = () => setSidebarOpen(prev => !prev);

  // ==================== API CALLS ====================
  const getApiBaseUrl = () => process.env.REACT_APP_API_BASE_URL || '';

 const fetchUserProfile = useCallback(() => {
  try {
    // Try to get user profile from localStorage (where your login stores it)
    let userProfileData = null;
    const userProfileStr = localStorage.getItem('userProfile');
    
    if (userProfileStr) {
      try {
        userProfileData = JSON.parse(userProfileStr);
        console.debug('[AccountApproval] User profile from localStorage:', userProfileData);
      } catch (e) {
        console.error('[AccountApproval] Error parsing userProfile:', e);
      }
    }
    
    // Try multiple possible keys for name (your login might use different field names)
    const userName = userProfileData?.name || 
                     userProfileData?.full_name ||
                     userProfileData?.username ||
                     userProfileData?.fullName ||
                     userProfileData?.staff_name ||
                     userProfileData?.employee_name ||
                     localStorage.getItem('userName') || 
                     localStorage.getItem('name') ||
                     localStorage.getItem('fullName') ||
                     'Account Officer';
    
    // Try multiple possible keys for phone
    const phoneNumber = userProfileData?.phone || 
                        userProfileData?.phone_number ||
                        userProfileData?.mobile ||
                        localStorage.getItem('phoneNumber') || 
                        localStorage.getItem('phone') ||
                        '';
    
    // Try multiple possible keys for user type
    const userType = userProfileData?.user_type || 
                     userProfileData?.role ||
                     userProfileData?.userType ||
                     localStorage.getItem('role') || 
                     localStorage.getItem('user_type') ||
                     '';
    
    console.debug('[AccountApproval] Extracted user info:', {
      name: userName,
      phone: phoneNumber,
      user_type: userType,
      rawProfile: userProfileData
    });
    
    // Validate and set user profile
    const hasValidRole = validateAccountUser(userType);
    
    if (!hasValidRole && userType !== '') {
      console.warn('[AccountApproval] User does not have account role:', userType);
      setSnackbar({ 
        open: true, 
        message: `Access: ${userType} role. Account or Cashier role required.`, 
        severity: 'error' 
      });
    }
    
    setUserProfile({
      phone: phoneNumber || '',
      name: userName || 'Account Officer',
      department: HARDCODED_DEPARTMENT,
      user_type: userType,
    });
  } catch (err) {
    console.error('[AccountApproval] Error loading user profile:', err);
  }
}, []);

  const fetchPurchaseRequests = useCallback(async () => {
    const startTime = performance.now();
    console.debug('[AccountApproval] Fetching purchase requests for account approval...');

    try {
      setLoading(true);
      const baseUrl = getApiBaseUrl();
      
      // Fetch all pending and account_approved requests
      const response = await axios.get(`${baseUrl}/hotel/inventory/purchase_requests.php`);
      
      console.debug('[AccountApproval] API Response:', response.data);

      if (response.data.success) {
        const requests = response.data.data || [];
        
        // Process requests with items
        const processedRequests = requests.map(req => ({
          ...req,
          items: req.items || [],
          total_items: req.items?.length || 0,
          total_amount: parseFloat(req.total_amount) || 0,
        }));
        
        setAllRequests(processedRequests);
        
        // Separate by status for tabs
        const pending = processedRequests.filter(req => req.status === 'pending');
        const approved = processedRequests.filter(req => 
          req.status === 'account_approved' || 
          req.status === 'gm_approved' || 
          req.status === 'partially_received' || 
          req.status === 'completed'
        );
        const rejected = processedRequests.filter(req => req.status === 'rejected');
        
        setPendingRequests(pending);
        setApprovedRequests(approved);
        setRejectedRequests(rejected);
        setStats(response.data.stats);
        
        // Calculate summary data
        const totalValue = processedRequests.reduce((sum, req) => sum + (parseFloat(req.total_amount) || 0), 0);
        const totalRequests = processedRequests.length;
        const approvedCount = approved.length;
        
        setSummaryData({
          totalValue,
          averageValue: totalRequests > 0 ? totalValue / totalRequests : 0,
          approvalRate: totalRequests > 0 ? (approvedCount / totalRequests) * 100 : 0,
          totalRequests,
        });
        
        console.debug(`[AccountApproval] Loaded ${processedRequests.length} requests in ${(performance.now() - startTime).toFixed(2)}ms`);
        console.debug(`[AccountApproval] Pending: ${pending.length}, Approved: ${approved.length}, Rejected: ${rejected.length}`);
      } else {
        setError(response.data.message);
        console.error('[AccountApproval] API returned error:', response.data.message);
      }
    } catch (err) {
      console.error('[AccountApproval] Fetch error:', err);
      setError('Failed to load purchase requests');
      setSnackbar({ open: true, message: 'Failed to load requests', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  // ==================== APPROVAL HANDLERS ====================
  const handleApproveRequest = async () => {
    if (!selectedRequest) {
      console.error('[AccountApproval] No request selected for approval');
      return;
    }

    logSecurityEvent('ACCOUNT_APPROVAL_ATTEMPT', {
      request_id: selectedRequest.id,
      po_number: selectedRequest.po_number,
      total_amount: selectedRequest.total_amount,
      remark_length: approvalRemark.length,
    });

    setLoading(true);

    try {
      const baseUrl = getApiBaseUrl();
      const response = await axios.put(`${baseUrl}/hotel/inventory/purchase_requests.php`, {
        id: selectedRequest.id,
        action: 'account_approve',
        remark: sanitizeString(approvalRemark),
        staff_name: userProfile.name || 'Account Department',
      });

      console.debug('[AccountApproval] Approval response:', response.data);

      if (response.data.success) {
        setSnackbar({ 
          open: true, 
          message: `✓ Purchase Request #${selectedRequest.po_number} approved by Account!`, 
          severity: 'success' 
        });
        setApproveDialogOpen(false);
        setApprovalRemark('');
        setViewDialogOpen(false);
        fetchPurchaseRequests();
        logSecurityEvent('ACCOUNT_APPROVAL_SUCCESS', { 
          request_id: selectedRequest.id, 
          po_number: selectedRequest.po_number 
        });
      } else {
        setSnackbar({ open: true, message: response.data.message, severity: 'error' });
        logSecurityEvent('ACCOUNT_APPROVAL_FAILED', { 
          request_id: selectedRequest.id, 
          error: response.data.message 
        });
      }
    } catch (err) {
      console.error('[AccountApproval] Error approving request:', err);
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.message || 'Failed to approve request', 
        severity: 'error' 
      });
      logSecurityEvent('ACCOUNT_APPROVAL_ERROR', { 
        request_id: selectedRequest.id, 
        error: err.message 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRejectRequest = async () => {
    if (!selectedRequest) {
      console.error('[AccountApproval] No request selected for rejection');
      return;
    }

    if (!rejectionReason || rejectionReason.trim().length < 5) {
      setSnackbar({ 
        open: true, 
        message: 'Please provide a detailed reason for rejection (minimum 5 characters)', 
        severity: 'error' 
      });
      return;
    }

    logSecurityEvent('ACCOUNT_REJECTION_ATTEMPT', {
      request_id: selectedRequest.id,
      po_number: selectedRequest.po_number,
      reason_length: rejectionReason.length,
    });

    setLoading(true);

    try {
      const baseUrl = getApiBaseUrl();
      const response = await axios.put(`${baseUrl}/hotel/inventory/purchase_requests.php`, {
        id: selectedRequest.id,
        action: 'account_reject',
        remark: sanitizeString(rejectionReason),
        staff_name: userProfile.name || 'Account Department',
      });

      console.debug('[AccountApproval] Rejection response:', response.data);

      if (response.data.success) {
        setSnackbar({ 
          open: true, 
          message: `✗ Purchase Request #${selectedRequest.po_number} rejected by Account`, 
          severity: 'warning' 
        });
        setRejectDialogOpen(false);
        setRejectionReason('');
        setViewDialogOpen(false);
        fetchPurchaseRequests();
        logSecurityEvent('ACCOUNT_REJECTION_SUCCESS', { 
          request_id: selectedRequest.id, 
          po_number: selectedRequest.po_number 
        });
      } else {
        setSnackbar({ open: true, message: response.data.message, severity: 'error' });
      }
    } catch (err) {
      console.error('[AccountApproval] Error rejecting request:', err);
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.message || 'Failed to reject request', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  // ==================== PRINT PURCHASE ORDER ====================
  const handlePrintPO = () => {
    if (!selectedRequest) return;

    logSecurityEvent('PURCHASE_ORDER_PRINT_ATTEMPT', {
      request_id: selectedRequest.id,
      po_number: selectedRequest.po_number,
      printed_by: userProfile.name,
    });

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      setSnackbar({ 
        open: true, 
        message: 'Please allow pop-ups to print the purchase order', 
        severity: 'error' 
      });
      return;
    }

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Purchase Order - ${selectedRequest.po_number}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: 'Times New Roman', Times, serif;
            margin: 0;
            padding: 20px;
            background: white;
          }
          .po-container {
            max-width: 1000px;
            margin: 0 auto;
            border: 1px solid #ddd;
            padding: 30px;
            box-shadow: 0 0 10px rgba(0,0,0,0.1);
          }
          .header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 3px solid #C84646;
            padding-bottom: 15px;
          }
          .header h1 {
            margin: 0;
            color: #C84646;
            font-size: 28px;
            letter-spacing: 2px;
          }
          .header h3 {
            margin: 8px 0;
            color: #555;
            font-size: 16px;
          }
          .header p {
            margin: 5px 0;
            color: #666;
            font-size: 12px;
          }
          .po-info {
            display: flex;
            justify-content: space-between;
            margin-bottom: 25px;
            padding: 15px;
            background: #f8f9fa;
            border-radius: 5px;
          }
          .po-info div {
            margin: 5px 0;
          }
          .po-info strong {
            color: #333;
          }
          .approved-badge {
            background: #4caf50;
            color: white;
            padding: 5px 15px;
            border-radius: 20px;
            font-size: 12px;
            display: inline-block;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin: 20px 0;
          }
          th, td {
            border: 1px solid #ddd;
            padding: 12px;
            text-align: left;
          }
          th {
            background-color: #f5f5f5;
            font-weight: bold;
            font-size: 14px;
          }
          td {
            font-size: 13px;
          }
          .total-row {
            font-weight: bold;
            background-color: #f5f5f5;
          }
          .total-row td {
            font-size: 14px;
          }
          .notes-section {
            margin: 20px 0;
            padding: 15px;
            background: #f8f9fa;
            border-left: 4px solid #C84646;
          }
          .signatures {
            display: flex;
            justify-content: space-between;
            margin-top: 40px;
          }
          .signature-line {
            text-align: center;
            width: 30%;
          }
          .signature-line hr {
            margin: 30px 0 8px;
            border: none;
            border-top: 1px solid #333;
            width: 100%;
          }
          .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #ddd;
            text-align: center;
            font-size: 11px;
            color: #666;
          }
          .approval-stamp {
            text-align: right;
            margin-top: 20px;
            padding: 10px;
            border: 2px solid #4caf50;
            border-radius: 5px;
            background: #e8f5e9;
          }
          @media print {
            body { padding: 0; }
            .no-print { display: none; }
            .po-container { box-shadow: none; border: 1px solid #ddd; }
          }
        </style>
      </head>
      <body>
        <div class="po-container">
          <div class="no-print" style="text-align: right; margin-bottom: 15px;">
            <button onclick="window.print();" style="padding: 8px 20px; background: #C84646; color: white; border: none; border-radius: 5px; cursor: pointer; font-size: 14px;">
              🖨️ Print / Save as PDF
            </button>
          </div>
          
          <div class="header">
            <h1>PURCHASE ORDER</h1>
            <h3>Elegantè Lounge & Suites</h3>
            <p>Plot 123, Abuja City Centre, Federal Capital Territory, Nigeria</p>
            <p>Tel: +234 802 123 4567 | Email: purchases@elegante.com</p>
          </div>
          
          <div class="po-info">
            <div>
              <strong>PO Number:</strong> ${selectedRequest.po_number}<br>
              <strong>Date Issued:</strong> ${formatDate(selectedRequest.created_at)}<br>
              <strong>Delivery Date:</strong> ${selectedRequest.delivery_date ? formatDate(selectedRequest.delivery_date) : 'TBD'}
            </div>
            <div>
              <strong>Requested By:</strong> ${sanitizeString(selectedRequest.requested_by_name || 'N/A')}<br>
              <strong>Department:</strong> ${sanitizeString(selectedRequest.department || HARDCODED_DEPARTMENT)}<br>
              <strong>Status:</strong> <span class="approved-badge">${selectedRequest.account_approved_at ? '✓ ACCOUNT APPROVED' : 'PENDING'}</span>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>S/N</th>
                <th>Item Description</th>
                <th>Specification</th>
                <th align="right">Quantity</th>
                <th>Unit</th>
                <th align="right">Unit Price (₦)</th>
                <th align="right">Total (₦)</th>
              </tr>
            </thead>
            <tbody>
              ${(selectedRequest.items || []).map((item, idx) => `
                <tr>
                  <td>${idx + 1}</td>
                  <td>${sanitizeString(item.item_name)}</td>
                  <td>${sanitizeString(item.specification || '-')}</td>
                  <td align="right">${sanitizeNumber(item.quantity).toLocaleString()}</td>
                  <td>${sanitizeString(item.unit_of_measurement || 'pcs')}</td>
                  <td align="right">${sanitizeNumber(item.unit_price).toLocaleString()}</td>
                  <td align="right">${sanitizeNumber(item.total_price).toLocaleString()}</td>
                </tr>
              `).join('')}
              <tr class="total-row">
                <td colspan="6" align="right"><strong>GRAND TOTAL:</strong></td>
                <td align="right"><strong>₦${sanitizeNumber(selectedRequest.total_amount).toLocaleString()}</strong></td>
               </tr>
            </tbody>
          </table>

          ${selectedRequest.notes ? `
            <div class="notes-section">
              <strong>📝 Notes/Special Instructions:</strong><br>
              <p style="margin-top: 8px;">${sanitizeString(selectedRequest.notes)}</p>
            </div>
          ` : ''}

          ${selectedRequest.account_approved_at ? `
            <div class="approval-stamp">
              <strong>✓ ACCOUNT APPROVED</strong><br>
              <small>Approved by: ${selectedRequest.account_approved_by}</small><br>
              <small>Date: ${formatDateTime(selectedRequest.account_approved_at)}</small>
            </div>
          ` : ''}

          <div class="signatures">
            <div class="signature-line">
              <hr>
              <small>Requested By</small><br>
              <small style="font-size: 10px;">${selectedRequest.requested_by_name || '_________________'}</small>
            </div>
            <div class="signature-line">
              <hr>
              <small>Account Approval</small><br>
              <small style="font-size: 10px;">${selectedRequest.account_approved_by ? selectedRequest.account_approved_by : '_________________'}</small>
              ${selectedRequest.account_approved_at ? `<br><small style="font-size: 9px;">${formatDate(selectedRequest.account_approved_at)}</small>` : ''}
            </div>
            <div class="signature-line">
              <hr>
              <small>GM Approval</small><br>
              <small style="font-size: 10px;">${selectedRequest.gm_approved_by ? selectedRequest.gm_approved_by : '_________________'}</small>
            </div>
          </div>

          <div class="footer">
            <p>This is a computer-generated document. No signature is required for validity.</p>
            <p>For inquiries, please contact the Purchasing Department at purchases@elegante.com</p>
            <p>Generated on: ${new Date().toLocaleString()}</p>
          </div>
        </div>
        <script>
          console.log('Print window ready');
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
  };

  // ==================== HELPER FUNCTIONS FOR UI ====================
  const getStatusChip = (status) => {
    const config = REQUEST_STATUSES[status] || { label: status, color: '#757575', icon: PendingIcon };
    return (
      <Chip
        label={config.label}
        size="small"
        icon={<config.icon sx={{ fontSize: 16 }} />}
        sx={{ bgcolor: alpha(config.color, 0.1), color: config.color, fontWeight: 'medium' }}
      />
    );
  };

  const getActionBadge = (status) => {
    const config = REQUEST_STATUSES[status];
    if (!config) return null;
    return (
      <Chip
        label={config.action}
        size="small"
        variant="outlined"
        sx={{ fontSize: '11px', height: '20px' }}
      />
    );
  };

  // Filter requests based on search, status, department, date range
  const getFilteredRequests = useMemo(() => {
    let requests = [];
    
    switch (activeTab) {
      case 0:
        requests = [...pendingRequests];
        break;
      case 1:
        requests = [...approvedRequests];
        break;
      case 2:
        requests = [...rejectedRequests];
        break;
      default:
        requests = [...allRequests];
    }
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      requests = requests.filter(req =>
        req.po_number?.toLowerCase().includes(term) ||
        req.requested_by_name?.toLowerCase().includes(term) ||
        req.department?.toLowerCase().includes(term)
      );
    }
    
    // Apply department filter
    if (departmentFilter !== 'all') {
      requests = requests.filter(req => 
        (req.department || '').toLowerCase() === departmentFilter.toLowerCase()
      );
    }
    
    // Apply date range filter
    if (dateRange.start) {
      requests = requests.filter(req => new Date(req.created_at) >= new Date(dateRange.start));
    }
    if (dateRange.end) {
      const endDate = new Date(dateRange.end);
      endDate.setHours(23, 59, 59);
      requests = requests.filter(req => new Date(req.created_at) <= endDate);
    }
    
    return requests;
  }, [activeTab, pendingRequests, approvedRequests, rejectedRequests, allRequests, searchTerm, departmentFilter, dateRange]);

  // Get unique departments for filter
  const uniqueDepartments = useMemo(() => {
    const depts = new Set();
    allRequests.forEach(req => {
      if (req.department) depts.add(req.department);
    });
    return Array.from(depts).sort();
  }, [allRequests]);

  // ==================== INITIALIZATION ====================
  useEffect(() => {
    fetchUserProfile();
    fetchPurchaseRequests();
  }, [fetchUserProfile, fetchPurchaseRequests]);

  // Add this debug useEffect to check what's actually in localStorage
useEffect(() => {
  console.log('[DEBUG ACCOUNT] Checking localStorage for user data:');
  
  // Check all possible keys
  console.log('  role:', localStorage.getItem('role'));
  console.log('  userProfile:', localStorage.getItem('userProfile'));
  console.log('  user_type:', localStorage.getItem('user_type'));
  console.log('  userType:', localStorage.getItem('userType'));
  
  // Parse userProfile if exists
  const userProfileStr = localStorage.getItem('userProfile');
  if (userProfileStr) {
    try {
      const profile = JSON.parse(userProfileStr);
      console.log('  userProfile.user_type:', profile.user_type);
      console.log('  Full profile:', profile);
    } catch(e) {
      console.error('  Error parsing userProfile:', e);
    }
  }
  
  // Check what validateAccountUser returns
  const userType = localStorage.getItem('role') || 
                   (userProfileStr ? JSON.parse(userProfileStr)?.user_type : null) ||
                   '';
  console.log('  Current userType being used:', userType);
  console.log('  validateAccountUser result:', validateAccountUser(userType));
}, []);

  // ==================== RENDER COMPONENTS ====================
  const StatCard = ({ title, value, subtitle, color, icon: Icon, trend }) => (
    <Card sx={{
      borderRadius: 3,
      height: '100%',
      boxShadow: '0px 2px 8px rgba(0,0,0,0.1)',
      transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
      '&:hover': { transform: 'translateY(-3px)', boxShadow: theme.shadows[8] },
      cursor: 'pointer',
    }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight="bold" sx={{ color, mt: 1 }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                {subtitle}
              </Typography>
            )}
            {trend !== undefined && (
              <Chip 
                size="small" 
                label={trend >= 0 ? `+${trend}%` : `${trend}%`}
                sx={{ mt: 1, bgcolor: trend >= 0 ? alpha('#4caf50', 0.1) : alpha('#f44336', 0.1), color: trend >= 0 ? '#4caf50' : '#f44336' }}
              />
            )}
          </Box>
          <Avatar sx={{ bgcolor: alpha(color, 0.1), color, width: 56, height: 56 }}>
            <Icon sx={{ fontSize: 32 }} />
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  const RequestCard = ({ request, onView, onApprove, onReject }) => (
    <Card sx={{ 
      mb: 2, 
      borderRadius: 2,
      transition: 'all 0.2s ease',
      '&:hover': { boxShadow: theme.shadows[4] },
      borderLeft: `4px solid ${REQUEST_STATUSES[request.status]?.color || '#757575'}`
    }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              {request.po_number}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {request.requested_by_name} • {request.department || HARDCODED_DEPARTMENT}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {getStatusChip(request.status)}
            {getActionBadge(request.status)}
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="h6" color="#C84646" fontWeight="bold">
              {formatCurrency(request.total_amount)}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {request.total_items} item(s) • Created: {formatDate(request.created_at)}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="View Details">
              <IconButton size="small" onClick={() => onView(request)}>
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {request.status === 'pending' && (
              <>
                <Tooltip title="Approve">
                  <IconButton size="small" sx={{ color: '#4caf50' }} onClick={() => onApprove(request)}>
                    <CheckCircleIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Reject">
                  <IconButton size="small" sx={{ color: '#f44336' }} onClick={() => onReject(request)}>
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
            <Tooltip title="Print PO">
              <IconButton size="small" onClick={() => {
                setSelectedRequest(request);
                handlePrintPO();
              }}>
                <PrintIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        
        {request.account_remark?.length > 0 && (
          <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #eee' }}>
            <Typography variant="caption" color="textSecondary" display="flex" alignItems="center" gap={0.5}>
              <CommentIcon sx={{ fontSize: 12 }} />
              Latest remark: {request.account_remark[request.account_remark.length - 1]?.remark?.substring(0, 60)}...
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  // Loading state
  if (loading && allRequests.length === 0) {
    return (
      <Box sx={{ display: 'flex', height: '100vh' }}>
        <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <Box sx={{ flexGrow: 1, p: 3, ml: isMobile ? 0 : sidebarOpen ? 30 : 5, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
          <CircularProgress size={60} sx={{ color: '#C84646' }} />
          <Typography sx={{ mt: 2, color: '#666' }}>Loading purchase requests for account approval...</Typography>
        </Box>
      </Box>
    );
  }

  // Check if user has account role
  if (!validateAccountUser(userProfile.user_type) && userProfile.user_type !== '') {
    return (
      <Box sx={{ display: 'flex', height: '100vh' }}>
        <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <Box sx={{ flexGrow: 1, p: 3, ml: isMobile ? 0 : sidebarOpen ? 30 : 5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Alert severity="error" sx={{ maxWidth: 500 }}>
            <Typography variant="h6" gutterBottom>Access Denied</Typography>
            <Typography>You do not have permission to access this page. Account role is required.</Typography>
            <Typography variant="caption" display="block" sx={{ mt: 2 }}>Your role: {userProfile.user_type || 'Not set'}</Typography>
          </Alert>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#f5f5f5' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <Box sx={{
        flexGrow: 1,
        p: 3,
        ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
        maxWidth: '1400px',
        mx: 'auto',
        width: '100%',
        overflow: 'auto',
        height: '100vh',
      }}>
        <TopNav
          onLogout={() => {
            logSecurityEvent('USER_LOGOUT', {});
            localStorage.clear();
            window.location.href = '/';
          }}
          onNotificationClick={() => console.debug('[AccountApproval] Notification clicked')}
          toggleSidebar={toggleSidebar}
        />

        {/* Header Section */}
        <Paper elevation={0} sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #C84646 0%, #a33a3a 100%)',
          color: 'white',
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h4" fontWeight="bold" sx={{ fontFamily: 'Times New Roman, serif' }}>
                Account Approval Dashboard
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                Review and approve purchase requests before they proceed to GM
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <Chip 
                  label={`Welcome, ${userProfile.name || 'Account Officer'}`} 
                  size="small" 
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                />
                <Chip 
                  label={`Role: ${userProfile.user_type?.toUpperCase() || 'ACCOUNT'}`} 
                  size="small" 
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                />
              </Box>
            </Box>
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
              onClick={fetchPurchaseRequests}
              disabled={loading}
              sx={{ bgcolor: 'white', color: '#C84646', '&:hover': { bgcolor: '#f5f5f5' }, textTransform: 'none' }}
            >
              Refresh
            </Button>
          </Box>
        </Paper>

        {/* Statistics Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard 
              title="Pending Approval" 
              value={stats?.pending || 0} 
              color="#ff9800" 
              icon={PendingIcon}
              trend={stats?.pending > 5 ? 10 : -5}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard 
              title="Total Value Pending" 
              value={formatCurrency(pendingRequests.reduce((sum, r) => sum + (r.total_amount || 0), 0))} 
              color="#ff9800" 
              icon={MoneyIcon}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard 
              title="Account Approved" 
              value={stats?.account_approved || 0} 
              color="#2196f3" 
              icon={CheckCircleIcon}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard 
              title="Approval Rate" 
              value={`${summaryData.approvalRate.toFixed(1)}%`} 
              subtitle={`${summaryData.totalRequests} total requests`}
              color="#4caf50" 
              icon={VerifiedUserIcon}
            />
          </Grid>
        </Grid>

        {/* Quick Stats Row */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: alpha('#C84646', 0.1), color: '#C84646' }}>
                <ReceiptIcon />
              </Avatar>
              <Box>
                <Typography variant="body2" color="textSecondary">Total Purchase Value</Typography>
                <Typography variant="h6" fontWeight="bold">{formatCurrency(summaryData.totalValue)}</Typography>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: alpha('#C84646', 0.1), color: '#C84646' }}>
                <InventoryIcon />
              </Avatar>
              <Box>
                <Typography variant="body2" color="textSecondary">Average Request Value</Typography>
                <Typography variant="h6" fontWeight="bold">{formatCurrency(summaryData.averageValue)}</Typography>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: alpha('#C84646', 0.1), color: '#C84646' }}>
                <HistoryIcon />
              </Avatar>
              <Box>
                <Typography variant="body2" color="textSecondary">Total Items Requested</Typography>
                <Typography variant="h6" fontWeight="bold">
                  {allRequests.reduce((sum, r) => sum + (r.total_items || 0), 0)}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Tabs for different request statuses */}
        <Paper sx={{ borderRadius: 3, overflow: 'hidden', mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#fafafa' }}
            variant="fullWidth"
          >
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PendingIcon sx={{ fontSize: 18 }} />
                  Pending Approval
                  <Badge badgeContent={pendingRequests.length} color="warning" sx={{ ml: 1 }} />
                </Box>
              } 
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon sx={{ fontSize: 18 }} />
                  Approved
                  <Badge badgeContent={approvedRequests.length} color="success" sx={{ ml: 1 }} />
                </Box>
              } 
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CancelIcon sx={{ fontSize: 18 }} />
                  Rejected
                  <Badge badgeContent={rejectedRequests.length} color="error" sx={{ ml: 1 }} />
                </Box>
              } 
            />
          </Tabs>

          {/* Filters */}
          <Box sx={{ p: 2, bgcolor: '#fafafa', borderBottom: 1, borderColor: 'divider' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search by PO number, requester..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Department</InputLabel>
                  <Select
                    value={departmentFilter}
                    onChange={(e) => setDepartmentFilter(e.target.value)}
                    label="Department"
                  >
                    <MenuItem value="all">All Departments</MenuItem>
                    {uniqueDepartments.map(dept => (
                      <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  size="small"
                  type="date"
                  fullWidth
                  label="From Date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  size="small"
                  type="date"
                  fullWidth
                  label="To Date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </Box>
        </Paper>

        {/* Requests List */}
        <Box>
          {getFilteredRequests.length === 0 ? (
            <Paper sx={{ p: 5, textAlign: 'center', borderRadius: 3 }}>
              <PendingIcon sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
              <Typography variant="h6" color="textSecondary">
                {activeTab === 0 ? 'No pending requests for approval' : 
                 activeTab === 1 ? 'No approved requests found' : 
                 'No rejected requests found'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {activeTab === 0 ? 'All purchase requests requiring your approval will appear here.' :
                 'Use the filters above to search for specific requests.'}
              </Typography>
            </Paper>
          ) : (
            getFilteredRequests.map(request => (
              <RequestCard
                key={request.id}
                request={request}
                onView={(req) => {
                  setSelectedRequest(req);
                  setViewDialogOpen(true);
                }}
                onApprove={(req) => {
                  setSelectedRequest(req);
                  setApprovalRemark('');
                  setApproveDialogOpen(true);
                }}
                onReject={(req) => {
                  setSelectedRequest(req);
                  setRejectionReason('');
                  setRejectDialogOpen(true);
                }}
              />
            ))
          )}
        </Box>

        {/* Results Summary */}
        {getFilteredRequests.length > 0 && (
          <Paper sx={{ p: 2, mt: 2, borderRadius: 2, bgcolor: '#fafafa' }}>
            <Typography variant="body2">
              <strong>Showing:</strong> {getFilteredRequests.length} request(s)
              {searchTerm && ` matching "${searchTerm}"`}
              {departmentFilter !== 'all' && ` from ${departmentFilter} department`}
              {dateRange.start && ` from ${formatDate(dateRange.start)}`}
              {dateRange.end && ` to ${formatDate(dateRange.end)}`}
            </Typography>
          </Paper>
        )}

        <footer style={{ textAlign: 'center', padding: '20px', marginTop: 'auto' }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body2" color="textSecondary">
            Designed by Elegantè IT - Account Approval Management System
          </Typography>
        </footer>
      </Box>

      {/* ==================== VIEW DETAILS DIALOG ==================== */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Box>
              <Typography variant="h6">Purchase Request Details</Typography>
              <Typography variant="body2" color="textSecondary">{selectedRequest?.po_number}</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Print PO">
                <IconButton onClick={handlePrintPO} sx={{ color: '#C84646' }}>
                  <PrintIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Close">
                <IconButton onClick={() => setViewDialogOpen(false)}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Stack spacing={3} sx={{ mt: 1 }}>
              {/* Status Timeline */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>Approval Timeline</Typography>
                <Timeline position="alternate">
                  <TimelineItem>
                    <TimelineOppositeContent color="textSecondary">
                      {formatDate(selectedRequest.created_at)}
                    </TimelineOppositeContent>
                    <TimelineSeparator>
                      <TimelineDot color="primary">
                        <ReceiptIcon fontSize="small" />
                      </TimelineDot>
                      <TimelineConnector />
                    </TimelineSeparator>
                    <TimelineContent>
                      <Typography variant="body2" fontWeight="bold">Request Submitted</Typography>
                      <Typography variant="caption" color="textSecondary">
                        By {selectedRequest.requested_by_name}
                      </Typography>
                    </TimelineContent>
                  </TimelineItem>
                  
                  <TimelineItem>
                    <TimelineOppositeContent color="textSecondary">
                      {selectedRequest.account_approved_at ? formatDate(selectedRequest.account_approved_at) : 'Pending'}
                    </TimelineOppositeContent>
                    <TimelineSeparator>
                      <TimelineDot color={selectedRequest.account_approved_at ? "success" : "warning"}>
                        {selectedRequest.account_approved_at ? <CheckCircleIcon fontSize="small" /> : <PendingIcon fontSize="small" />}
                      </TimelineDot>
                      <TimelineConnector />
                    </TimelineSeparator>
                    <TimelineContent>
                      <Typography variant="body2" fontWeight="bold">Account Approval</Typography>
                      {selectedRequest.account_approved_at ? (
                        <Typography variant="caption" color="textSecondary">
                          Approved by {selectedRequest.account_approved_by}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="warning.main">
                          Awaiting your approval
                        </Typography>
                      )}
                    </TimelineContent>
                  </TimelineItem>
                  
                  <TimelineItem>
                    <TimelineOppositeContent color="textSecondary">
                      {selectedRequest.gm_approved_at ? formatDate(selectedRequest.gm_approved_at) : 'Pending'}
                    </TimelineOppositeContent>
                    <TimelineSeparator>
                      <TimelineDot color={selectedRequest.gm_approved_at ? "success" : "grey"}>
                        {selectedRequest.gm_approved_at ? <VerifiedIcon fontSize="small" /> : <PendingIcon fontSize="small" />}
                      </TimelineDot>
                      <TimelineConnector />
                    </TimelineSeparator>
                    <TimelineContent>
                      <Typography variant="body2" fontWeight="bold">GM Approval</Typography>
                      {selectedRequest.gm_approved_at && (
                        <Typography variant="caption" color="textSecondary">
                          Approved by {selectedRequest.gm_approved_by}
                        </Typography>
                      )}
                    </TimelineContent>
                  </TimelineItem>
                  
                  <TimelineItem>
                    <TimelineOppositeContent color="textSecondary">
                      {selectedRequest.status === 'completed' ? formatDate(selectedRequest.updated_at) : 'Pending'}
                    </TimelineOppositeContent>
                    <TimelineSeparator>
                      <TimelineDot color={selectedRequest.status === 'completed' ? "success" : "grey"}>
                        <LocalShippingIcon fontSize="small" />
                      </TimelineDot>
                    </TimelineSeparator>
                    <TimelineContent>
                      <Typography variant="body2" fontWeight="bold">Store Receipt</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {selectedRequest.status === 'completed' ? 'All items received' : 
                         selectedRequest.status === 'partially_received' ? 'Partial receipt' : 'Awaiting receipt'}
                      </Typography>
                    </TimelineContent>
                  </TimelineItem>
                </Timeline>
              </Paper>

              {/* Request Information */}
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Request Information</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 1 }}>
                      <Typography variant="caption" color="textSecondary">PO Number:</Typography>
                      <Typography variant="body2">{selectedRequest.po_number}</Typography>
                      <Typography variant="caption" color="textSecondary">Requester:</Typography>
                      <Typography variant="body2">{selectedRequest.requested_by_name} ({selectedRequest.requested_by_phone})</Typography>
                      <Typography variant="caption" color="textSecondary">Department:</Typography>
                      <Typography variant="body2">{selectedRequest.department || HARDCODED_DEPARTMENT}</Typography>
                      <Typography variant="caption" color="textSecondary">Created:</Typography>
                      <Typography variant="body2">{formatDateTime(selectedRequest.created_at)}</Typography>
                      <Typography variant="caption" color="textSecondary">Delivery Date:</Typography>
                      <Typography variant="body2">{selectedRequest.delivery_date ? formatDate(selectedRequest.delivery_date) : 'Not specified'}</Typography>
                    </Box>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Financial Summary</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 1 }}>
                      <Typography variant="caption" color="textSecondary">Total Items:</Typography>
                      <Typography variant="body2">{selectedRequest.total_items || selectedRequest.items?.length || 0}</Typography>
                      <Typography variant="caption" color="textSecondary">Total Amount:</Typography>
                      <Typography variant="body2" fontWeight="bold" color="#C84646">{formatCurrency(selectedRequest.total_amount)}</Typography>
                      <Typography variant="caption" color="textSecondary">Status:</Typography>
                      <Box>{getStatusChip(selectedRequest.status)}</Box>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>

              {/* Items Table */}
              <Typography variant="subtitle1" fontWeight="bold">Items Ordered</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell>S/N</TableCell>
                      <TableCell>Item</TableCell>
                      <TableCell>Specification</TableCell>
                      <TableCell align="right">Quantity</TableCell>
                      <TableCell>Unit</TableCell>
                      <TableCell align="right">Unit Price</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(selectedRequest.items || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography variant="body2" color="textSecondary">No items found for this purchase request.</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      (selectedRequest.items || []).map((item, idx) => (
                        <TableRow key={item.id || idx}>
                          <TableCell>{idx + 1}</TableCell>
                          <TableCell>{sanitizeString(item.item_name)}</TableCell>
                          <TableCell>{sanitizeString(item.specification || '-')}</TableCell>
                          <TableCell align="right">{sanitizeNumber(item.quantity).toLocaleString()}</TableCell>
                          <TableCell>{sanitizeString(item.unit_of_measurement || 'pcs')}</TableCell>
                          <TableCell align="right">{formatCurrency(item.unit_price)}</TableCell>
                          <TableCell align="right">{formatCurrency(item.total_price)}</TableCell>
                        </TableRow>
                      ))
                    )}
                    {(selectedRequest.items || []).length > 0 && (
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell colSpan={6} align="right"><strong>GRAND TOTAL:</strong></TableCell>
                        <TableCell align="right"><strong>{formatCurrency(selectedRequest.total_amount)}</strong></TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Notes */}
              {selectedRequest.notes && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold">Notes/Special Instructions:</Typography>
                  <Typography variant="body2">{selectedRequest.notes}</Typography>
                </Paper>
              )}

              {/* Account Remarks History */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold">Account Remarks History</Typography>
                  {selectedRequest.account_remark?.length > 0 && (
                    <Chip size="small" label={`${selectedRequest.account_remark.length} remarks`} variant="outlined" />
                  )}
                </Box>
                {selectedRequest.account_remark?.length > 0 ? (
                  selectedRequest.account_remark.map((remark, idx) => (
                    <Paper key={idx} variant="outlined" sx={{ p: 1.5, mt: 1, bgcolor: alpha('#2196f3', 0.05), borderLeft: `3px solid #2196f3` }}>
                      <Typography variant="body2">
                        <strong>{remark.added_by}:</strong> {remark.remark}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" display="block">
                        {formatDateTime(remark.added_at)}
                      </Typography>
                    </Paper>
                  ))
                ) : (
                  <Typography variant="body2" color="textSecondary">No account remarks yet.</Typography>
                )}
              </Paper>

              {/* Auditor Remarks (if any) */}
              {selectedRequest.auditor_remark?.length > 0 && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Auditor Remarks</Typography>
                  {selectedRequest.auditor_remark.map((remark, idx) => (
                    <Paper key={idx} variant="outlined" sx={{ p: 1, mt: 1, bgcolor: '#fafafa' }}>
                      <Typography variant="caption" display="block">
                        <strong>{remark.added_by}:</strong> {remark.remark}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" display="block">
                        {formatDateTime(remark.added_at)}
                      </Typography>
                    </Paper>
                  ))}
                </Paper>
              )}

              {/* Action Buttons for Pending Requests */}
              {selectedRequest.status === 'pending' && (
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<CancelIcon />}
                    onClick={() => {
                      setViewDialogOpen(false);
                      setRejectionReason('');
                      setRejectDialogOpen(true);
                    }}
                  >
                    Reject Request
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<CheckCircleIcon />}
                    onClick={() => {
                      setViewDialogOpen(false);
                      setApprovalRemark('');
                      setApproveDialogOpen(true);
                    }}
                    sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#45a049' } }}
                  >
                    Approve Request
                  </Button>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ==================== APPROVE DIALOG ==================== */}
      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#4caf50', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon /> Approve Purchase Request
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Alert severity="info" icon={<InfoIcon />}>
              <strong>PO Number:</strong> {selectedRequest?.po_number}<br />
              <strong>Total Amount:</strong> {formatCurrency(selectedRequest?.total_amount)}<br />
              <strong>Requester:</strong> {selectedRequest?.requested_by_name}<br />
              <strong>Items:</strong> {selectedRequest?.items?.length || 0} item(s)
            </Alert>
            
            <Alert severity="warning">
              <strong>⚠️ Important:</strong> Approving this request will move it to GM for final approval. Please verify all details are correct.
            </Alert>
            
            <TextField
              label="Approval Remark (Optional)"
              multiline
              rows={3}
              fullWidth
              value={approvalRemark}
              onChange={(e) => setApprovalRemark(e.target.value)}
              placeholder="Add any remarks or observations for GM..."
              helperText="This remark will be visible to GM and stored in the approval history"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleApproveRequest} 
            variant="contained" 
            sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#45a049' } }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Confirm Approval'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ==================== REJECT DIALOG ==================== */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#f44336', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CancelIcon /> Reject Purchase Request
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Alert severity="error" icon={<WarningIcon />}>
              <strong>PO Number:</strong> {selectedRequest?.po_number}<br />
              <strong>Total Amount:</strong> {formatCurrency(selectedRequest?.total_amount)}<br />
              <strong>Requester:</strong> {selectedRequest?.requested_by_name}
            </Alert>
            
            <Alert severity="warning">
              <strong>⚠️ Warning:</strong> This action cannot be undone. The requester will be notified of the rejection.
            </Alert>
            
            <TextField
              label="Rejection Reason *"
              multiline
              rows={4}
              fullWidth
              required
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Please provide a detailed reason for rejection. This will help the requester understand and improve future requests."
              error={rejectionReason && rejectionReason.length < 5}
              helperText={rejectionReason && rejectionReason.length < 5 ? "Please provide at least 5 characters" : "Required - Provide detailed reason"}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleRejectRequest} 
            variant="contained" 
            color="error"
            disabled={loading || !rejectionReason || rejectionReason.length < 5}
          >
            {loading ? <CircularProgress size={24} /> : 'Confirm Rejection'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PurshaseOrdeAccountApproval;